import type { YardiGuestCardRow, YardiGuestCardApplication } from '../types'
import { formatDisplayName } from './yardiInviteUi'
import { canInviteGuestCard } from './yardiGuestCardStatus'

/** Application-level fields shared by occupants returned from the same guest card. */
export function guestCardApplicationKey(row: YardiGuestCardRow): string {
  return [
    row.unitNumber ?? '',
    row.leaseStartDate ?? '',
    row.targetMoveInDate ?? '',
    row.expectedMoveInDate ?? '',
    row.leaseRentAmount ?? '',
    row.applicationDate ?? '',
    row.floorPlan ?? '',
  ].join('|')
}

export function guestCardOccupantName(row: YardiGuestCardRow): string {
  const raw = [row.namePrefix, row.firstName, row.middleName, row.lastName].filter(Boolean).join(' ')
  return formatDisplayName(raw)
}

export function guestCardIsPrimary(row: YardiGuestCardRow): boolean {
  return String(row.type ?? '').toUpperCase() === 'SELF'
}

/** All occupants that belong to the same guest-card application as `anchor`. */
export function groupGuestCardOccupants(
  invites: YardiGuestCardRow[],
  anchor: YardiGuestCardRow,
): YardiGuestCardRow[] {
  const key = guestCardApplicationKey(anchor)
  const grouped = invites.filter((row) => guestCardApplicationKey(row) === key)
  return grouped.length > 0 ? grouped : [anchor]
}

export function guestCardProspectKey(row: YardiGuestCardRow): string {
  return String(row.prospectId ?? '').toUpperCase()
}

export function getGuestCardPrimaryApplicant(applicants: YardiGuestCardRow[]): YardiGuestCardRow {
  if (applicants.length === 0) return {}
  return applicants.find((row) => guestCardIsPrimary(row)) ?? applicants[0]
}

export function guestCardApplicationExpandKey(application: YardiGuestCardApplication, index: number): string {
  const primary = getGuestCardPrimaryApplicant(application.applicants)
  const prospectKey = guestCardProspectKey(primary)
  return prospectKey ? `${prospectKey}-${index}` : `application-${index}`
}

/**
 * Show View only when every occupant has Automated=No
 * (manual invites do not block View).
 */
export function canInviteGuestCardApplication(application: YardiGuestCardApplication): boolean {
  const applicants = application.applicants
  if (applicants.length === 0) return false
  return applicants.every((row) => canInviteGuestCard(row))
}

/** FAILED row with an existing yardi application — same retry API as invite list. */
export function canRetryGuestCardApplication(application: YardiGuestCardApplication): boolean {
  return application.state === 'FAILED' && application.yardi_application_id != null
}
